import { GridCoordinates, HexCoordinates } from '../engine/field/FieldTypes'

export class IndexTransforms {
  // ==================== GRID TRANSFORMATIONS ====================

  /**
   * Convert 1D index to 2D grid coordinates
   */
  static indexToGrid(index: number, gridSize: number): GridCoordinates {
    const row = Math.floor(index / gridSize)
    const col = index % gridSize
    return { row, col }
  }

  /**
   * Convert 2D grid coordinates to 1D index
   */
  static gridToIndex(row: number, col: number, gridSize: number): number {
    return row * gridSize + col
  }

  /**
   * Convert 1D index to centered grid coordinates (origin at center)
   */
  static indexToCenteredGrid(index: number, gridSize: number): GridCoordinates {
    const { row, col } = this.indexToGrid(index, gridSize)
    const centerOffset = (gridSize - 1) / 2
    
    return {
      row: row - centerOffset,
      col: col - centerOffset
    }
  }

  /**
   * Get grid coordinates for rectangular grid with different width/height
   */
  static indexToRectGrid(
    index: number,
    width: number,
    height: number
  ): GridCoordinates {
    const row = Math.floor(index / width)
    const col = index % width
    return { row, col }
  }

  // ==================== HEXAGONAL TRANSFORMATIONS ====================

  /**
   * Convert 1D index to hexagonal ring coordinates
   */
  static indexToHex(index: number): HexCoordinates {
    if (index === 0) {
      return { ring: 0, positionInRing: 0 }
    }

    // Calculate which ring this index belongs to
    let ring = 1
    let totalPointsInPreviousRings = 1 // Center point
    
    while (totalPointsInPreviousRings + ring * 6 <= index) {
      totalPointsInPreviousRings += ring * 6
      ring++
    }

    const positionInRing = index - totalPointsInPreviousRings
    return { ring, positionInRing }
  }

  /**
   * Convert hexagonal ring coordinates to 1D index
   */
  static hexToIndex(ring: number, positionInRing: number): number {
    if (ring === 0) return 0
    
    // Calculate total points in all previous rings
    let totalPoints = 1 // Center point
    for (let r = 1; r < ring; r++) {
      totalPoints += r * 6
    }
    
    return totalPoints + positionInRing
  }

  /**
   * Get total number of points in hexagonal pattern up to given ring
   */
  static getTotalHexPoints(maxRing: number): number {
    if (maxRing === 0) return 1
    
    let total = 1 // Center point
    for (let ring = 1; ring <= maxRing; ring++) {
      total += ring * 6
    }
    return total
  }

  /**
   * Calculate hexagonal coordinates (cube coordinates)
   */
  static indexToHexCube(index: number): { q: number; r: number; s: number } {
    const { ring, positionInRing } = this.indexToHex(index)
    
    if (ring === 0) {
      return { q: 0, r: 0, s: 0 }
    }

    // Convert ring/position to cube coordinates
    const pointsPerSide = ring
    const sideIndex = Math.floor(positionInRing / pointsPerSide)
    const positionInSide = positionInRing % pointsPerSide
    
    let q = 0, r = 0, s = 0
    
    switch (sideIndex) {
      case 0: // Top-right side
        q = ring - positionInSide
        r = positionInSide
        s = -ring
        break
      case 1: // Right side
        q = -positionInSide
        r = ring
        s = positionInSide - ring
        break
      case 2: // Bottom-right side
        q = -ring
        r = ring - positionInSide
        s = positionInSide
        break
      case 3: // Bottom-left side
        q = positionInSide - ring
        r = -positionInSide
        s = ring
        break
      case 4: // Left side
        q = positionInSide
        r = -ring
        s = ring - positionInSide
        break
      case 5: // Top-left side
        q = ring
        r = positionInSide - ring
        s = -positionInSide
        break
    }
    
    return { q, r, s }
  }

  // ==================== DISTANCE CALCULATIONS ====================

  /**
   * Calculate Manhattan distance in grid
   */
  static gridManhattanDistance(
    coord1: GridCoordinates,
    coord2: GridCoordinates
  ): number {
    return Math.abs(coord1.row - coord2.row) + Math.abs(coord1.col - coord2.col)
  }

  /**
   * Calculate Euclidean distance in grid
   */
  static gridEuclideanDistance(
    coord1: GridCoordinates,
    coord2: GridCoordinates
  ): number {
    const deltaRow = coord1.row - coord2.row
    const deltaCol = coord1.col - coord2.col
    return Math.sqrt(deltaRow * deltaRow + deltaCol * deltaCol)
  }

  /**
   * Calculate distance in hexagonal grid
   */
  static hexDistance(coord1: HexCoordinates, coord2: HexCoordinates): number {
    const cube1 = this.indexToHexCube(this.hexToIndex(coord1.ring, coord1.positionInRing))
    const cube2 = this.indexToHexCube(this.hexToIndex(coord2.ring, coord2.positionInRing))
    
    return (Math.abs(cube1.q - cube2.q) + 
            Math.abs(cube1.r - cube2.r) + 
            Math.abs(cube1.s - cube2.s)) / 2
  }

  // ==================== NEIGHBOR CALCULATIONS ====================

  /**
   * Get all neighbors of a grid cell
   */
  static getGridNeighbors(
    coord: GridCoordinates,
    gridSize: number,
    includeDiagonal: boolean = false
  ): GridCoordinates[] {
    const neighbors: GridCoordinates[] = []
    const directions = includeDiagonal 
      ? [[-1,-1], [-1,0], [-1,1], [0,-1], [0,1], [1,-1], [1,0], [1,1]]
      : [[-1,0], [0,-1], [0,1], [1,0]]
    
    for (const [dr, dc] of directions) {
      const newRow = coord.row + dr
      const newCol = coord.col + dc
      
      if (newRow >= 0 && newRow < gridSize && newCol >= 0 && newCol < gridSize) {
        neighbors.push({ row: newRow, col: newCol })
      }
    }
    
    return neighbors
  }

  /**
   * Get all neighbors of a hexagonal cell
   */
  static getHexNeighbors(coord: HexCoordinates): HexCoordinates[] {
    const index = this.hexToIndex(coord.ring, coord.positionInRing)
    const cube = this.indexToHexCube(index)
    
    const directions = [
      { q: 1, r: 0, s: -1 },
      { q: 1, r: -1, s: 0 },
      { q: 0, r: -1, s: 1 },
      { q: -1, r: 0, s: 1 },
      { q: -1, r: 1, s: 0 },
      { q: 0, r: 1, s: -1 }
    ]
    
    const neighbors: HexCoordinates[] = []
    
    for (const dir of directions) {
      const neighborCube = {
        q: cube.q + dir.q,
        r: cube.r + dir.r,
        s: cube.s + dir.s
      }
      
      // Convert back to hex coordinates
      // This is a simplified conversion - in practice you'd need proper cube-to-hex conversion
      const neighborIndex = this.cubeToIndex(neighborCube)
      if (neighborIndex >= 0) {
        neighbors.push(this.indexToHex(neighborIndex))
      }
    }
    
    return neighbors
  }

  // ==================== UTILITY FUNCTIONS ====================

  /**
   * Check if grid coordinates are valid
   */
  static isValidGridCoordinate(coord: GridCoordinates, gridSize: number): boolean {
    return coord.row >= 0 && coord.row < gridSize && 
           coord.col >= 0 && coord.col < gridSize
  }

  /**
   * Get center coordinates of grid
   */
  static getGridCenter(gridSize: number): GridCoordinates {
    const center = (gridSize - 1) / 2
    return { row: center, col: center }
  }

  /**
   * Convert spiral index to approximate position
   */
  static indexToSpiralPosition(index: number, totalCount: number): number {
    return index / totalCount
  }

  // ==================== PRIVATE HELPER METHODS ====================

  /**
   * Convert cube coordinates back to index (helper method)
   */
  private static cubeToIndex(cube: { q: number; r: number; s: number }): number {
    // This is a simplified implementation
    // In practice, you'd need to implement proper cube-to-hex-to-index conversion
    const ring = Math.max(Math.abs(cube.q), Math.abs(cube.r), Math.abs(cube.s))
    
    if (ring === 0) return 0
    
    // Calculate position in ring based on cube coordinates
    // This is a complex calculation that depends on which side of the hexagon
    // For now, return a placeholder
    return -1 // Indicates invalid/not implemented
  }
} 